import { useMemo } from 'react';
import { StyledSpinner } from '@tryhackme/thm-ui-components';
import VisGraph, { type Options, Edge, Network, Node } from 'react-vis-graph-wrapper';
import { NetworkInstanceStatus } from 'src/common/interfaces';
import { LoadingMessage } from '../loading-message/loading-message';
import { StyledDiagramContainer, StyledNetworkLoaderWrapper, StyledOverlay, StyledOverlayHeader, StyledOverlaySubHeader } from './diagram-display.styles';
export const OPTIONS = (readOnly: boolean): Options => ({
  layout: {
    hierarchical: {
      enabled: true,
      direction: 'UD',
      nodeSpacing: 155,
      sortMethod: 'directed'
    }
  },
  nodes: {
    borderWidth: 0,
    size: 40,
    color: {
      border: '#222',
      background: '#3E475A',
      ...(readOnly ? {} : {
        highlight: {
          background: '#1153E4'
        },
        hover: {
          background: '#3dc2ff'
        }
      })
    },
    font: {
      color: '#ffffff',
      face: 'monospace',
      size: 14,
      strokeWidth: 1,
      strokeColor: '#222'
    },
    shape: 'box'
  },
  edges: {
    arrows: {
      to: {
        enabled: false
      }
    },
    color: {
      highlight: 'red'
    },
    hoverWidth: 0,
    selectionWidth: 0
  },
  physics: false,
  interaction: {
    hover: true,
    dragView: false,
    keyboard: {
      enabled: false,
      bindToWindow: false
    },
    navigationButtons: false,
    zoomView: false
  },
  height: '540px'
});
interface DiagramDisplayProps {
  nodes: Node[];
  edges: Edge[];
  networkRef?: React.MutableRefObject<Network | null>;
  isRequestingDeploy?: boolean;
  onSelectNodes?: (selectedNodes: number[]) => void;
  onSelectEdges?: (selectedEdges: string[]) => void;
  onNewEdge?: (newEdge: Edge) => void;
  readOnly?: boolean;
  disableOverlay?: boolean;
  status?: NetworkInstanceStatus;
}
export const DiagramDisplay: React.FC<DiagramDisplayProps> = ({
  disableOverlay = false,
  nodes,
  edges,
  isRequestingDeploy,
  onSelectNodes,
  onSelectEdges,
  onNewEdge,
  readOnly = false,
  status,
  networkRef
}) => {
  const graph = {
    nodes,
    edges
  };
  const options = useMemo(() => ({
    ...OPTIONS(readOnly),
    ...(readOnly ? {} : {
      manipulation: {
        enabled: false,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        addEdge: (edgeData: Edge, _callback: never) => {
          onNewEdge?.(edgeData);
        }
      }
    })
  }), [onNewEdge, readOnly]);
  return <StyledDiagramContainer id="network-diagram" data-sentry-element="StyledDiagramContainer" data-sentry-component="DiagramDisplay" data-sentry-source-file="diagram-display.tsx">
      {disableOverlay ? null : <>
          {!status && <StyledOverlay>
              <StyledOverlayHeader>Press &apos;Start&apos; to begin</StyledOverlayHeader>
            </StyledOverlay>}
          {status === NetworkInstanceStatus.PAUSED && <StyledOverlay>
              <StyledOverlayHeader>Network Paused</StyledOverlayHeader>
              <StyledOverlaySubHeader>Press &apos;Start&apos; to resume</StyledOverlaySubHeader>
            </StyledOverlay>}
          {isRequestingDeploy || status === NetworkInstanceStatus.DEPLOYING && <StyledOverlay>
                <StyledNetworkLoaderWrapper>
                  <StyledSpinner size="10rem" color="rgb(238, 239, 242)" />
                  <LoadingMessage />
                </StyledNetworkLoaderWrapper>
              </StyledOverlay>}
        </>}
      <VisGraph graph={graph} options={options} getNetwork={(network: Network) => {
      if (network && networkRef) {
        const currentRef = networkRef;
        currentRef.current = network;
      }
    }} events={{
      selectNode: (event: {
        nodes: number[];
      }) => {
        onSelectNodes?.(event.nodes);
      },
      selectEdge: (event: {
        edges: string[];
      }) => {
        onSelectEdges?.(event.edges);
      }
    }} data-sentry-element="VisGraph" data-sentry-source-file="diagram-display.tsx" />
    </StyledDiagramContainer>;
};